import type { ModerationConfig, StaffRole } from '@planuze/admin-contracts';
import {
  InlineAlert,
  Page,
  PageContent,
  PageDescription,
  PageHeader,
  PageTitle,
  PageTitleBlock,
  Spinner,
  toast,
} from '@planuze/ui';
import { ModerationConfigForm } from '../components/moderation/moderation-config-form.js';
import { ScannerRuntimeStatusCard } from '../components/moderation/scanner-runtime-status-card.js';
import { useAdminLocale } from '../content/locale-provider.js';
import { useModerationConfigContent } from '../content/sections/moderation-config.js';
import { useModerationConfig, useUpdateModerationConfig } from '../hooks/use-moderation-config.js';
import { useStaffSession } from '../hooks/use-staff-session.js';
import { configServiceErrorMessage } from '../lib/config-service-error.js';

const MANAGE_ROLES: readonly StaffRole[] = ['super_admin', 'support'];

export function ModerationConfigPage() {
  const copy = useModerationConfigContent();
  const { locale } = useAdminLocale();
  const session = useStaffSession();
  const canManage = session !== null && MANAGE_ROLES.includes(session.role);
  const { data, isLoading, isError } = useModerationConfig();
  const update = useUpdateModerationConfig();

  const onSave = (next: ModerationConfig): void => {
    update.mutate(next, {
      onSuccess: () => toast.success(copy.saveSuccess),
      onError: (err) => toast.error(configServiceErrorMessage(err, copy.saveErrorFallback, locale)),
    });
  };

  return (
    <Page>
      <PageHeader>
        <PageTitleBlock>
          <PageTitle>{copy.pageTitle}</PageTitle>
          <PageDescription>{copy.pageDescription}</PageDescription>
        </PageTitleBlock>
      </PageHeader>
      <PageContent className="space-y-6">
        <InlineAlert variant="info">{copy.infoAlert}</InlineAlert>
        <ScannerRuntimeStatusCard
          {...(data !== undefined && { policyEnabled: data.scanner.enabled })}
        />
        {isError ? (
          <InlineAlert variant="destructive">{copy.loadError}</InlineAlert>
        ) : isLoading || data === undefined ? (
          <div className="grid place-items-center p-12">
            <Spinner size="lg" />
          </div>
        ) : (
          /* `key` = assinatura do config live: remonta o form (re-seeda o estado local) só
             quando o conteúdo do servidor muda (pós-save/refetch), preservando edições em
             andamento quando um refetch traz o MESMO conteúdo. */
          <ModerationConfigForm
            key={JSON.stringify(data)}
            config={data}
            canManage={canManage}
            saving={update.isPending}
            onSave={onSave}
          />
        )}
      </PageContent>
    </Page>
  );
}
